import React, { useState } from 'react';
import {
  Box,
  Button,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActions,
  Paper,
  Divider,
  Chip,
  Stack,
} from '@mui/material';
import {
  Chat as ChatIcon,
  Support as SupportIcon,
  ShoppingCart as ShoppingCartIcon,
  Dashboard as DashboardIcon,
  Smartphone as MobileIcon,
  Settings as SettingsIcon,
  Clear as ClearIcon,
  Add as AddIcon,
} from '@mui/icons-material';

import ChatWidget from '../components/ChatWidget';
import FormOptionsDrawer from '../components/FormOptionsDrawer';
import ChatStatusBar from '../components/ChatWidgets/ChatStatusBar';

// Example flows for different use cases
const supportFlow = {
  id: 'support_flow',
  name: 'Customer Support',
  nodes: [
    {
      id: 'start',
      type: 'start',
      data: { label: 'Start Support' },
    },
    {
      id: 'ai_agent',
      type: 'ai-agent',
      data: {
        label: 'Support Agent',
        prompt: 'You are a helpful customer support agent. Help the customer with their issue.',
      },
    },
    {
      id: 'form',
      type: 'form',
      data: {
        label: 'Issue Details',
        fields: [
          {
            name: 'issue_type', label: 'Issue Type', type: 'select', options: ['Technical', 'Billing', 'General'],
          },
          { name: 'description', label: 'Description', type: 'textarea' },
        ],
      },
    },
  ],
};

const salesFlow = {
  id: 'sales_flow',
  name: 'Sales Assistant',
  nodes: [
    {
      id: 'start',
      type: 'start',
      data: { label: 'Start Sales' },
    },
    {
      id: 'ai_agent',
      type: 'ai-agent',
      data: {
        label: 'Sales Agent',
        prompt: 'You are a helpful sales assistant. Help the customer find the right product.',
      },
    },
    {
      id: 'form',
      type: 'form',
      data: {
        label: 'Customer Info',
        fields: [
          {
            name: 'budget', label: 'Budget Range', type: 'select', options: ['$0-100', '$100-500', '$500+'],
          },
          { name: 'use_case', label: 'Use Case', type: 'textarea' },
        ],
      },
    },
  ],
};

function ChatWidgetDemo() {
  // State for different chat variants
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [panelOpen, setPanelOpen] = useState(false);
  const [supportChatOpen, setSupportChatOpen] = useState(false);
  const [salesChatOpen, setSalesChatOpen] = useState(false);

  // State for form options
  const [formOptionsOpen, setFormOptionsOpen] = useState(false);
  const [selectedModel, setSelectedModel] = useState('openai');
  const [temperature, setTemperature] = useState(0.7);
  const [playAudio, setPlayAudio] = useState(true);
  const [darkMode, setDarkMode] = useState(false);
  const [selectedFlow, setSelectedFlow] = useState(null);

  // State for status bar
  const [isConnected, setIsConnected] = useState(true);
  const [isAudioEnabled, setIsAudioEnabled] = useState(true);
  const [isListening, setIsListening] = useState(false);

  const handleMessage = (message, type) => {
    console.log('Chat Message:', { message, type, timestamp: new Date().toISOString() });
  };

  const handleFlowComplete = (flowState) => {
    console.log('Flow Completed:', flowState);
    // You can handle flow completion here
    // For example, show a success message or redirect
  };

  return (
    <Box sx={{ p: 3, maxWidth: 1200, mx: 'auto' }}>
      <Typography variant="h3" component="h1" gutterBottom>
        ChatWidget Demo
      </Typography>

      <Typography variant="body1" color="text.secondary" paragraph>
        This page demonstrates different ways to use the ChatWidget component in your application.
      </Typography>

      {/* Control Bar */}
      <Paper sx={{ p: 2, mb: 3, bgcolor: 'grey.50' }}>
        <Typography variant="h6" gutterBottom>
          Chat Controls
        </Typography>
        <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={() => {
              // Clear all chat messages
              console.log('New conversation started');
            }}
          >
            New Chat
          </Button>
          <Button
            variant="outlined"
            startIcon={<ClearIcon />}
            onClick={() => {
              // Clear all chat messages
              console.log('Chat cleared');
            }}
          >
            Clear Chat
          </Button>
          <Button
            variant="outlined"
            startIcon={<SettingsIcon />}
            onClick={() => setFormOptionsOpen(true)}
          >
            Settings
          </Button>
        </Stack>
      </Paper>

      {/* Status Bar */}
      <Paper sx={{ p: 1, mb: 3, bgcolor: 'background.paper' }}>
        <ChatStatusBar
          isConnected={isConnected}
          isAudioEnabled={isAudioEnabled}
          isListening={isListening}
          onToggleAudio={() => setIsAudioEnabled(!isAudioEnabled)}
          onToggleMic={() => setIsListening(!isListening)}
          onShowOptions={() => setFormOptionsOpen(true)}
        />
      </Paper>

      <Grid container spacing={3}>
        {/* Drawer Variant Demo */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h5" component="h2" gutterBottom>
                <ChatIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                Drawer Variant
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                Opens as a side drawer. Perfect for customer support or help sections.
              </Typography>
              <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
                <Chip label="Responsive" color="primary" size="small" />
                <Chip label="Overlay" color="secondary" size="small" />
                <Chip label="Customizable" color="info" size="small" />
              </Stack>
            </CardContent>
            <CardActions>
              <Button
                variant="contained"
                onClick={() => setDrawerOpen(true)}
                startIcon={<ChatIcon />}
              >
                Open Chat Drawer
              </Button>
            </CardActions>
          </Card>
        </Grid>

        {/* Panel Variant Demo */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h5" component="h2" gutterBottom>
                <DashboardIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                Panel Variant
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                Fixed panel that slides in from the side. Great for dashboards and admin panels.
              </Typography>
              <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
                <Chip label="Fixed Position" color="primary" size="small" />
                <Chip label="Full Height" color="secondary" size="small" />
                <Chip label="Persistent" color="info" size="small" />
              </Stack>
            </CardContent>
            <CardActions>
              <Button
                variant="outlined"
                onClick={() => setPanelOpen(!panelOpen)}
                startIcon={<DashboardIcon />}
              >
                Toggle Panel
              </Button>
            </CardActions>
          </Card>
        </Grid>

        {/* Support Chat Demo */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h5" component="h2" gutterBottom>
                <SupportIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                Support Chat
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                Pre-configured with a support flow. Includes issue tracking and form handling.
              </Typography>
              <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
                <Chip label="Flow Integration" color="primary" size="small" />
                <Chip label="Form Handling" color="secondary" size="small" />
                <Chip label="Issue Tracking" color="info" size="small" />
              </Stack>
            </CardContent>
            <CardActions>
              <Button
                variant="contained"
                color="success"
                onClick={() => setSupportChatOpen(true)}
                startIcon={<SupportIcon />}
              >
                Get Support
              </Button>
            </CardActions>
          </Card>
        </Grid>

        {/* Sales Chat Demo */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h5" component="h2" gutterBottom>
                <ShoppingCartIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                Sales Assistant
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                Sales-focused chat with product recommendations and lead capture.
              </Typography>
              <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
                <Chip label="Lead Capture" color="primary" size="small" />
                <Chip label="Product Recommendations" color="secondary" size="small" />
                <Chip label="Sales Flow" color="info" size="small" />
              </Stack>
            </CardContent>
            <CardActions>
              <Button
                variant="contained"
                color="warning"
                onClick={() => setSalesChatOpen(true)}
                startIcon={<ShoppingCartIcon />}
              >
                Talk to Sales
              </Button>
            </CardActions>
          </Card>
        </Grid>
      </Grid>

      <Divider sx={{ my: 4 }} />

      {/* Inline Chat Demo */}
      <Typography variant="h4" component="h2" gutterBottom>
        Inline Chat Example
      </Typography>
      <Typography variant="body1" paragraph>
        The chat widget can be embedded directly in the page content.
      </Typography>

      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Product Information
        </Typography>
        <Typography variant="body2" paragraph>
          This is an example of how you can embed a chat widget inline with your content.
          Users can ask questions about the product while reading the information.
        </Typography>

        <Box sx={{ height: 300, mt: 2 }}>
          <ChatWidget
            variant="inline"
            height={300}
            title="Product Assistant"
            showHeader
            showNotifications={false}
            showSidePanel={false}
            model={selectedModel}
            onMessage={handleMessage}
            customStyles={{
              border: '1px solid #e0e0e0',
              borderRadius: '8px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            }}
          />
        </Box>
      </Paper>

      {/* Mobile Demo */}
      <Typography variant="h4" component="h2" gutterBottom>
        <MobileIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
        Mobile-First Chat
      </Typography>
      <Typography variant="body1" paragraph>
        The chat widget automatically adapts to mobile devices with a bottom panel variant.
      </Typography>

      <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
        <Typography variant="body2" color="text.secondary">
          On mobile devices, the chat widget will automatically use a bottom panel layout
          for better touch interaction and screen space utilization.
        </Typography>
      </Paper>

      {/* Floating Chat Demo */}
      <Typography variant="h4" component="h2" gutterBottom sx={{ mt: 4 }}>
        Floating Chat Assistant
      </Typography>
      <Typography variant="body1" paragraph>
        A floating chat widget that starts collapsed and can be expanded when needed.
        Look for the chat icon in the bottom-right corner.
      </Typography>

      {/* Chat Widgets */}

      {/* Drawer Chat */}
      <ChatWidget
        variant="drawer"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        position="right"
        width={400}
        title="General Assistant"
        model={selectedModel}
        initialFlow={selectedFlow}
        onMessage={handleMessage}
        onFlowComplete={handleFlowComplete}
      />

      {/* Panel Chat */}
      <ChatWidget
        variant="panel"
        open={panelOpen}
        onClose={() => setPanelOpen(false)}
        position="right"
        width={450}
        height="100vh"
        title="Dashboard Assistant"
        model={selectedModel}
        initialFlow={selectedFlow}
        onMessage={handleMessage}
      />

      {/* Support Chat */}
      <ChatWidget
        variant="drawer"
        open={supportChatOpen}
        onClose={() => setSupportChatOpen(false)}
        position="right"
        width={450}
        title="Customer Support"
        model={selectedModel}
        initialFlow={selectedFlow || supportFlow}
        onFlowComplete={handleFlowComplete}
        onMessage={handleMessage}
        customStyles={{
          '& .MuiDrawer-paper': {
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
          },
        }}
      />

      {/* Sales Chat */}
      <ChatWidget
        variant="drawer"
        open={salesChatOpen}
        onClose={() => setSalesChatOpen(false)}
        position="left"
        width={450}
        title="Sales Assistant"
        model={selectedModel}
        initialFlow={selectedFlow || salesFlow}
        onFlowComplete={handleFlowComplete}
        onMessage={handleMessage}
        customStyles={{
          '& .MuiDrawer-paper': {
            background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
            color: 'white',
          },
        }}
      />

      {/* Floating Chat (collapsible, starts collapsed) */}
      <ChatWidget
        variant="floating"
        width={350}
        height={500}
        title="Help Assistant"
        collapsible
        initiallyCollapsed
        showNotifications={false}
        showSidePanel={false}
        model={selectedModel}
        initialFlow={selectedFlow}
        onMessage={handleMessage}
        customStyles={{
          borderRadius: '16px',
          boxShadow: '0 8px 32px rgba(0,0,0,0.15)',
        }}
      />

      {/* Form Options Drawer */}
      <FormOptionsDrawer
        open={formOptionsOpen}
        onClose={() => setFormOptionsOpen(false)}
        selectedModel={selectedModel}
        setSelectedModel={setSelectedModel}
        temperature={temperature}
        setTemperature={setTemperature}
        playAudio={playAudio}
        handlePlayAudioChange={setPlayAudio}
        darkMode={darkMode}
        onToggleTheme={() => setDarkMode(!darkMode)}
        selectedFlow={selectedFlow}
        setSelectedFlow={setSelectedFlow}
      />
    </Box>
  );
}

export default ChatWidgetDemo;
